import { useState, useRef } from "react";
import {
    Box,
    TableSortLabel,
    IconButton,
    Button,
    Select,
    MenuItem,
    TextField,
    Menu
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

const COMPARISON_OPTIONS = ["<", "=", ">"];

const NumberComparisonFilterHeader = ({
    label,
    columnKey,
    sortConfig,
    onSortChange,
    filterValue = {},
    onFilterChange,
    disableFilter = false
}) => {
    const isActive = sortConfig?.key === columnKey;
    const anchorRef = useRef(null);
    const [openFilter, setOpenFilter] = useState(false);
    const [selectOpen, setSelectOpen] = useState(false);

    const [comparison, setComparison] = useState(filterValue.operator || "=");
    const [value, setValue] = useState(filterValue.value || "");

    const handleSortClick = () => {
        const newDirection =
            isActive && sortConfig.direction === "asc" ? "desc" : "asc";
        onSortChange({ key: columnKey, direction: newDirection });
    };

    const handleFilterIconClick = () => {
        setComparison(filterValue.operator || "=");
        setValue(filterValue.value || "");
        setOpenFilter((prev) => !prev);
    };

    const handleApplyFilter = () => {
        onFilterChange(columnKey, { operator: comparison, value });
        setOpenFilter(false);
    };

    const handleClear = () => {
        setComparison("=");
        setValue("");
        onFilterChange(columnKey, {});
        setOpenFilter(false);
    };

    const fontStyle = {
        fontFamily: "Roboto",
        fontWeight: 400,
        fontSize: 10,
        letterSpacing: 0,
        verticalAlign: "middle",
        color: "#454545",
    };

    return (
        <Box display="flex" alignItems="center" gap={0.5}>
            <Box
                fontWeight="bold"
                sx={{
                    textTransform: "capitalize",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
                onClick={handleSortClick}
            >
                {label}
            </Box>

            <TableSortLabel
                active={isActive}
                direction={isActive ? sortConfig.direction : "asc"}
                onClick={handleSortClick}
                sx={{ cursor: "pointer" }}
            />

            {!disableFilter && (
                <>
                    <IconButton
                        size="small"
                        ref={anchorRef}
                        onClick={handleFilterIconClick}
                        sx={{ padding: 0 }}
                    >
                        <FilterListIcon
                            fontSize="small"
                            sx={{
                                color: filterValue?.value ? "primary.main" : "inherit",
                            }}
                        />
                    </IconButton>

                    <Menu
                        anchorEl={anchorRef.current}
                        open={openFilter}
                        onClose={() => setOpenFilter(false)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{
                            sx: {
                                p: 1,
                                boxShadow: 3,
                            },
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ height: 32 }}
                        >
                            <Select
                                value={comparison}
                                onChange={(e) => setComparison(e.target.value)}
                                onOpen={() => setSelectOpen(true)}
                                onClose={() => setSelectOpen(false)}
                                size="small"
                                sx={{
                                    height: 32,
                                    minWidth: 60,
                                    '.MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        ...fontStyle,
                                    },
                                }}
                            >
                                {COMPARISON_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option} sx={fontStyle}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>

                            <TextField
                                type="number"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                size="small"
                                placeholder="Value"
                                inputProps={{ min: 0 }}
                                sx={{
                                    width: 80,
                                    height: 32,
                                    '& .MuiInputBase-root': {
                                        height: 32,
                                        ...fontStyle,
                                    },
                                    '& input': {
                                        padding: '4px 8px',
                                    },
                                }}
                            />

                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleApplyFilter}
                                sx={{
                                    minWidth: 60,
                                    textTransform: "none",
                                    height: 32,
                                    ...fontStyle,
                                }}
                            >
                                Apply
                            </Button>

                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClear}
                                sx={{
                                    minWidth: 60,
                                    textTransform: "none",
                                    height: 32,
                                    ...fontStyle,
                                }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Menu>
                </>
            )}
        </Box>
    );
};

export default NumberComparisonFilterHeader;